import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Logo } from "../../assets/images";
import CustomButton from "../custom-button/custom-button";
import { RootState } from "../../redux/store/store";
import { useSelector, useDispatch } from "react-redux";
import { logOut } from "../../redux/store/isLogged-slices";
import { clearUserData } from "../../redux/store/userData-slices";
import { logoutService } from "../../services/auth-service";
import { IconButton, Drawer } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faSignOutAlt,
  faChevronDown,
  faChartBar,
  faBell,
  faCheck,
  faCircle,
  faUserPlus,
  faFolderPlus,
  faAnglesLeft,
  faAnglesRight
} from '@fortawesome/free-solid-svg-icons';
// import "./header.css";

interface HeaderProps {
  sidebarExpanded?: boolean;
  hasSidebar?: boolean;
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ sidebarExpanded = true, hasSidebar = false, onToggleSidebar }) => {
  const role = useSelector(
    (state: RootState) => state?.user?.userData?.role?.name
  );
  const userData = useSelector((state: RootState) => state.user.userData);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownMobileOpen, setDropdownMobileOpen] = useState(false);

  const navigation = useNavigate();
  const location = useLocation();
  const isLogged = useSelector((state: RootState) => state.auth.isLogged);
  const dispatch = useDispatch();
  const isDashboardRoute = location.pathname.startsWith("/dashboard");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMobileRef = useRef<HTMLDivElement>(null);

  const userFullName = userData?.fullName || '';
  const userInitials = userFullName
    ? userFullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
    : '';

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (!isDashboardRoute) {
        setShowHeader(currentScrollTop <= lastScrollTop);
      } else {
        setShowHeader(true);
      }
      setLastScrollTop(currentScrollTop <= 0 ? 0 : currentScrollTop);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollTop, location.pathname, isDashboardRoute]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Note: We've removed setDropdownOpen(false) from here because 
      // the new Drawer component handles its own backdrop/click-outside logic.
      if (dropdownMobileRef.current && !dropdownMobileRef.current.contains(target)) setDropdownMobileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logoutService().catch(() => undefined);
    dispatch(logOut());
    dispatch(clearUserData());

    setDropdownOpen(false);
    setDropdownMobileOpen(false);
    setIsMobileMenuOpen(false);

    navigation("/");
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) setDropdownMobileOpen(false);
  };

  const goToDashboard = () => {
    const path = role === "ROLE_TEACHER" ? "/subject" : "/home";
    navigation(path);
    setDropdownOpen(false);
    setDropdownMobileOpen(false);
    if (isMobileMenuOpen) toggleMobileMenu();
  };

  return (
    <>
      <header
        className={`z-50 h-[70px] lg:h-[80px] bg-white/80 backdrop-blur-md px-5 md:px-8 flex items-center fixed left-0 right-0 transition-all duration-500 ease-out border-b border-gray-100/50 shadow-sm ${showHeader ? 'top-0' : '-top-[80px]'}`}
      >
        {/* Logo & Sidebar Toggle Section */}
        <div className="flex items-center gap-4">
          <div 
            onClick={() => navigation("/")} 
            className="flex items-center gap-2 transition-transform cursor-pointer hover:scale-105"
          >
            <img
              src={Logo}
              alt="Logo"
              className="w-auto h-8 md:h-10 lg:h-12"
            />
            {/* Brand Name - Responsive visibility */}
            <span className="hidden sm:inline-block md:text-lg lg:text-xl font-black text-title tracking-tight tracking-[-0.02em] whitespace-nowrap">
              FOK <span className="italic text-primary">NJE7EK</span>
            </span>
          </div>

          {/* Desktop Sidebar Toggle - Only visible when hasSidebar is true */}
          {hasSidebar && (
            <button
              onClick={onToggleSidebar}
              className="items-center justify-center hidden w-10 h-10 ml-4 text-gray-400 transition-all duration-300 lg:flex rounded-xl bg-gray-50 hover:text-primary hover:bg-primary/10 group"
              title={sidebarExpanded ? "Réduire le menu" : "Agrandir le menu"}
            >
              <FontAwesomeIcon 
                icon={sidebarExpanded ? faAnglesLeft : faAnglesRight} 
                className={`w-3.5 h-3.5 transition-transform duration-500 ${sidebarExpanded ? 'group-hover:-translate-x-0.5' : 'group-hover:translate-x-0.5'}`}
              />
            </button>
          )}
        </div>

        {/* Navigation & Actions Area */}
        <div className="flex items-center gap-3 ml-auto md:gap-4">
          {!isLogged ? (
            <>
              {/* Desktop Only Navigation (Landing) */}
              <nav className="hidden mr-8 lg:block">
                <ul className="flex space-x-10">
                  {['home', 'about', 'free-courses', 'contact'].map((id) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (location.pathname !== "/") navigation("/");
                          else scrollToSection(id);
                        }}
                        className="text-[16px] font-semibold transition-colors text-title hover:text-primary relative group"
                      >
                        {id === 'home' ? 'Accueil' : id === 'about' ? 'À propos' : id === 'free-courses' ? 'Programmes' : 'Contact'}
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Desktop Only Auth Buttons (Landing) */}
              <div className="items-center hidden space-x-3 lg:flex">
                <CustomButton text="Se connecter" onClick={() => navigation("/login")} width="w-auto" className="bg-transparent hover:bg-gray-100 text-title font-bold rounded-xl h-[44px] px-6 text-[15px] transition-colors shadow-none" />
                <CustomButton text="S'inscrire" onClick={() => navigation("/role")} width="w-auto" className="bg-primary text-white h-[44px] rounded-xl px-7 text-[15px] font-bold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-all" />
              </div>
            </>
          ) : (
            /* Logged User Info - Desktop (Full) & Mobile (Compact Avatar) */
            <>
              <div className="items-center hidden lg:flex" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center px-3 py-2 space-x-3 transition-all duration-200 border border-gray-100 rounded-full hover:bg-gray-100 hover:shadow-md"
                >
                  <div className="flex items-center justify-center text-xs font-bold text-white border border-white rounded-full shadow-sm bg-primary w-9 h-9">
                    {userInitials}
                  </div>
                  <span className="text-sm font-bold text-title truncate max-w-[120px]">{userFullName}</span>
                  <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-400" />
                </button>
              </div>


            </>
          )}

          {/* Mobile Only: Single Menu Toggle Button */}
          <button
            onClick={() => {
              if (hasSidebar || isDashboardRoute) {
                // If in Dashboard, trigger the Sidebar Drawer
                window.dispatchEvent(new CustomEvent('sidebarToggleMobile'));
              } else {
                // Otherwise toggle Landing Menu
                toggleMobileMenu();
              }
            }}
            className="flex items-center justify-center p-2 transition-all lg:hidden w-11 h-11 md:w-12 md:h-12 text-primary bg-primary/5 hover:bg-primary/10 rounded-xl md:rounded-2xl active:scale-90"
            aria-label="Menu"
          >
            <MenuIcon className="w-7 h-7" />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <nav
        className={`lg:hidden fixed top-0 left-0 w-full h-screen bg-[#fcfcfc] z-[60] transition-transform duration-400 ease-in-out ${isMobileMenuOpen ? "translate-y-0" : "-translate-y-full"}`}
      >
        <div className="flex flex-col h-full px-8 pt-10 pb-8 overflow-y-auto">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between mb-10">
            <img src={Logo} alt="Logo" className="h-10" />
            <button onClick={toggleMobileMenu} className="p-2 text-primary">
              <CloseIcon className="w-10 h-10" />
            </button>
          </div>

          {/* User Info in Mobile Menu if logged in */}
          {isLogged && (
            <div className="flex items-center gap-4 p-6 mb-10 border bg-primary/5 rounded-3xl border-primary/10">
              <div className="flex items-center justify-center text-xl font-bold text-white shadow-lg w-14 h-14 bg-primary rounded-2xl">
                {userInitials}
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-title">{userFullName}</span>
                <span className="text-xs font-medium tracking-widest uppercase text-primary">{role?.replace("ROLE_", "")}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col space-y-6">
            {['home', 'about', 'free-courses', 'contact'].map((id) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleMobileMenu();
                  if (location.pathname !== "/") navigation("/");
                  else scrollToSection(id);
                }}
                className="text-2xl font-bold transition-colors text-title hover:text-primary"
              >
                {id === 'home' ? 'Accueil' : id === 'about' ? 'À propos' : id === 'free-courses' ? 'Programmes' : 'Contact'}
              </a>
            ))}
            
            {isLogged && (
              <>
                <Link 
                  to={role === "ROLE_TEACHER" ? "/subject" : "/home"} 
                  onClick={toggleMobileMenu}
                  className="text-2xl font-bold no-underline transition-colors text-title hover:text-primary"
                >
                  Tableau de bord
                </Link>
                <Link 
                  to="/dashboard/updateprofil" 
                  onClick={toggleMobileMenu}
                  className="text-2xl font-bold no-underline transition-colors text-title hover:text-primary"
                >
                  Mon Profil
                </Link>
              </>
            )}
          </div>

          <div className="flex flex-col gap-4 pt-8 mt-auto border-t border-gray-200">
            {isLogged ? (
               <CustomButton text="Déconnexion" onClick={handleLogout} width="w-full" className="font-bold border-none h-14 bg-rose-50 text-rose-500 rounded-2xl" />
            ) : (
              <>
                <CustomButton text="Se connecter" onClick={() => { navigation("/login"); toggleMobileMenu(); }} width="w-full" className="text-lg font-medium bg-transparent border-2 h-14 border-primary text-primary rounded-xl" />
                <CustomButton text="S'inscrire" onClick={() => { navigation("/role"); toggleMobileMenu(); }} width="w-full" className="text-lg font-medium shadow-md h-14 rounded-xl" />
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Shared Desktop Drawer for User Profile */}
      {isLogged && (
        <Drawer
          anchor="right"
          open={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          PaperProps={{
            sx: { width: { xs: '100%', sm: 400 }, backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' }
          }}
        >
          <div className="h-full bg-white/95 backdrop-blur-2xl border-l border-white/20 flex flex-col shadow-[-20px_0_80px_rgba(0,0,0,0.1)]">
            <div className="relative p-12 pb-10 bg-gradient-to-br from-primary/5 via-transparent to-transparent">
              <button onClick={() => setDropdownOpen(false)} className="absolute flex items-center justify-center w-10 h-10 text-gray-400 transition-all duration-300 top-8 right-8 rounded-2xl bg-gray-50 hover:bg-primary/10 hover:text-primary">
                <FontAwesomeIcon icon={faChevronDown} className="rotate-90" />
              </button>
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-[#09745f] to-[#07b98e] text-white rounded-[2.5rem] flex items-center justify-center text-4xl font-black shadow-2xl shadow-primary/20 border-4 border-white mb-6">
                  {userInitials}
                </div>
                <h3 className="mb-2 text-2xl font-black tracking-tight text-title">{userFullName}</h3>
                <div className="px-5 py-2 border rounded-full bg-primary/10 border-primary/20">
                  <span className="text-[10px] font-black text-primary uppercase tracking-[0.25em]">{role?.replace("ROLE_", "")}</span>
                </div>
              </div>
            </div>
            <div className="flex-1 px-8 py-4 space-y-4">
              <div className="p-1.5 bg-gray-50/50 rounded-[2.5rem] border border-gray-100/50">
                <Link to={role === "ROLE_TEACHER" ? "/subject" : "/home"} onClick={() => setDropdownOpen(false)} className="w-full flex items-center gap-5 px-6 py-5 text-[13px] font-black text-text/40 uppercase tracking-[0.2em] hover:bg-white hover:text-primary hover:shadow-xl hover:shadow-gray-200/50 rounded-[2rem] transition-all duration-500 group no-underline text-title">
                  <div className="flex items-center justify-center w-12 h-12 text-gray-400 transition-all bg-gray-100 rounded-2xl group-hover:bg-primary group-hover:text-white">
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                  <span>Tableau de bord</span>
                </Link>
                <Link to="/dashboard/updateprofil" onClick={() => setDropdownOpen(false)} className="w-full flex items-center gap-5 px-6 py-5 text-[13px] font-black text-text/40 uppercase tracking-[0.2em] hover:bg-white hover:text-primary hover:shadow-xl hover:shadow-gray-200/50 rounded-[2rem] transition-all duration-500 group no-underline text-title">
                  <div className="flex items-center justify-center w-12 h-12 text-gray-400 transition-all bg-gray-100 rounded-2xl group-hover:bg-primary group-hover:text-white">
                    <FontAwesomeIcon icon={faUserCircle} />
                  </div>
                  <span>Mon espace</span>
                </Link>
              </div>
            </div>
            <div className="p-8 mt-auto">
              <button onClick={handleLogout} className="w-full flex items-center justify-center gap-4 py-6 bg-rose-50 text-rose-500 rounded-[2.5rem] font-black text-[12px] uppercase tracking-[0.3em] hover:bg-rose-500 hover:text-white transition-all">
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
};

export default Header;